import React, {ReactElement} from "react";
import {connect} from "react-redux";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps,
    ScrollParams,
    Size,
} from "react-virtualized";
import {Nullable} from "@data-force/app";
import {
    getTranslate,
    Translate,
    TranslateFunction,
} from "react-localize-redux";
import classNames from "classnames";
import {
    Button, Dropdown, Menu, Tag,
} from "antd";
import {
    CaretDownOutlined, CaretUpOutlined, CloseCircleOutlined, DownOutlined, PlusOutlined,
} from "@ant-design/icons";
import {MenuInfo} from "rc-menu/lib/interface";
import StoreState from "../../interfaces/store/storeState";
import {Cursor} from "../../interfaces/store/cursor";
import {
    JobEntity,
    JobRequestParams,
    PhaseConfigNormalized,
    SelectedFiltersData,
} from "../../interfaces/store/actions/payload/job";
import {
    clearJobListData,
    getJobList,
    openJobEditor,
    setExternalJobUrl,
    setJobSearch,
    setJobTestType,
    setSelectedFilters,
    setSelectedJob,
    showDetailsDrawer,
    setFiltersDrawerVisibility,
    setSortDirection,
} from "../../store/actions/job";
import Spinner from "../common/Spinner/Spinner";
import {
    ClearJobListDataAction,
    GetJobListAction,
    OpenJobEditorAction,
    SetExternalJobUrlAction,
    SetJobSearchAction,
    SetJobTestTypeAction,
    SetSelectedFiltersAction,
    SetSelectedJobAction,
    ShowDetailsDrawerAction,
    SetFiltersDrawerVisibilityAction,
    SetSortDirectionAction,
} from "../../interfaces/store/actions/job";
import "./CardList.scss";
import SearchField from "../common/SearchField/SearchField";
import {CLICK_EVENT, DrawerTabs, ENTER_KEY} from "../../utils/constants";
import ContentEmpty from "../common/ContentEmpty/ContentEmpty";
import {Editors} from "../../interfaces/common";
import {getLocalePrefix, getOpeningUrl} from "../../utils/common";
import CardItem from "../CardItem/CardItem";
import {ContentType, NoResultType, SortDirection} from "../../enums/enums";
import {TabManager} from "../../utils/TabManager";

interface OwnProps {
    isMobile: boolean
    isTest: boolean
}

interface StateProps {
    translate: TranslateFunction
    cursor: Nullable<Cursor>
    isLoading: boolean
    list: JobEntity[]
    searchValue: Nullable<string>
    filter: string
    activeJobType: string
    activeTestType: string
    selectedJob: Nullable<JobEntity>
    openEditor: boolean
    editors: Editors
    phaseConfigList: PhaseConfigNormalized
    selectedFilters: SelectedFiltersData
    externalJobUrl: Nullable<string>
    isExternalUrlLoading: boolean
    sortDirection: string
}

interface DispatchProps {

    getJobList(payload?: JobRequestParams): GetJobListAction

    clearJobListData() : ClearJobListDataAction

    setFiltersDrawerVisibility(payload: boolean): SetFiltersDrawerVisibilityAction

    setJobSearch(payload: Nullable<string>): SetJobSearchAction

    setSelectedJob(payload: Nullable<JobEntity>): SetSelectedJobAction

    showDetailsDrawer(payload: string): ShowDetailsDrawerAction

    openJobEditor(payload: boolean): OpenJobEditorAction

    setJobTestType(payload: boolean): SetJobTestTypeAction

    setSelectedFilters(payload: SelectedFiltersData): SetSelectedFiltersAction

    setExternalJobUrl(payload: Nullable<string>): SetExternalJobUrlAction

    setSortDirection(payload: string): SetSortDirectionAction
}

type Props = StateProps & DispatchProps & OwnProps;

class CardList extends React.Component<Props> {
    private static readonly translationPath: string = "components.CardList.CardList";

    private static readonly cache = new CellMeasurerCache({
        defaultHeight: 223,
        fixedHeight: true,
        minWidth: 1000,
        keyMapper: () => 1,
    });

    constructor(props: Props) {
        super(props);

        this.onScrollHandler = this.onScrollHandler.bind(this);

        this.showFilterDrawer = this.showFilterDrawer.bind(this);

        this.getAdjustedRowHeight = this.getAdjustedRowHeight.bind(this);

        this.searchItems = this.searchItems.bind(this);

        this.noContentRenderer = this.noContentRenderer.bind(this);

        this.removeSelectedFilter = this.removeSelectedFilter.bind(this);
    }

    componentDidMount(): void {
        const {
            getJobList: getJobListHandler,
            setJobTestType: setJobTestHandler,
            filter,
            isTest,
        }: Props = this.props;
        getJobListHandler({filter, isTest});
        setJobTestHandler(isTest);
    }

    componentDidUpdate() {
        if (this.props.openEditor) {
            const {
                editors, phaseConfigList, selectedJob, externalJobUrl,
            }: Props = this.props;

            if (selectedJob) {
                let url: string;

                if (externalJobUrl) {
                    url = externalJobUrl;
                    this.props.setExternalJobUrl(null);
                } else {
                    url = getOpeningUrl(editors, phaseConfigList, selectedJob);
                }

                const tabManager: TabManager = TabManager.getInstance();
                tabManager.setUrl(url);
                this.props.openJobEditor(false);
            }
        }
    }

    componentWillUnmount(): void {
        this.props.clearJobListData();
    }

    /**
     * Scroll handler will load more projects to list
     * @param {number} scrollHeight
     * @param {number} clientHeight
     * @param {number} scrollTop
     */
    onScrollHandler({scrollHeight, clientHeight, scrollTop}: ScrollParams) {
        if ((Math.floor(scrollTop) === (scrollHeight - clientHeight)) || (Math.ceil(scrollTop) === (scrollHeight - clientHeight))) {
            const {
                getJobList: getJobListHandler,
                cursor,
                searchValue,
                filter,
                isTest,
                selectedFilters,
            }: Props = this.props;

            getJobListHandler({
                cursor,
                filter,
                isTest,
                title: searchValue,
                updateList: true,
                ...selectedFilters,
            });
        }
    }

    /**
     * On focus change set current element as focused
     * @param {React.FocusEvent} e
     */
    onFocusChange(e: React.FocusEvent): void {
        const target: HTMLElement = e.target as HTMLElement;
        target.focus();
    }

    /**
     * Updates global state in case direction is changed, and executes getJobList action with new sort direction
     * @param {string} key
     */
    onSortDirectionChange({key}: MenuInfo): void {
        const {
            sortDirection, searchValue, filter, isTest, selectedFilters,
        }: Props = this.props;

        if (sortDirection !== key) {
            this.props.setSortDirection(key);
            this.props.getJobList({
                title: searchValue,
                sortDirection: key,
                filter,
                isTest,
                ...selectedFilters,
            });
        }
    }

    /**
     * Depending on current folder and on screen width we adjust row height
     * @param {boolean} isMobile
     * @return {number}
     */
    getAdjustedRowHeight(isMobile: boolean): number {
        const mobileRowHeight: number = 270;
        const defaultRowHeight: number = 223;
        const altRowHeight: number = 239;

        const rowHeight: number = ContentType.AVAILABLE !== this.props.activeJobType ? altRowHeight : defaultRowHeight;
        return isMobile ? mobileRowHeight : rowHeight;
    }

    /**
     * Based on selected filters, we show row of tags with names of selected filters.
     * On tag click we remove currently clicked filter
     * @param {SelectedFiltersData} filters
     * @return {React.ReactNode}
     */
    getSelectedFilters(filters: SelectedFiltersData): React.ReactNode {
        return (
            <div className="df-item-list-selected-filters">
                <strong><Translate id={`${CardList.translationPath}.activeFilters.text`}/></strong>
                {Object.entries(filters).map(
                    ([filterGroup, filterValues]): React.ReactNode => (
                        !!filterValues.length && (
                            <span key={filterGroup} className="df-filter-tag-container">
                                <span className="df-filter-tag-label"><Translate id={`filters.${filterGroup}.text`}/></span>
                                {filterValues.map((currentFilter: string): React.ReactNode => (
                                    <Tag
                                        key={`${filterGroup}-${currentFilter}`}
                                        className="df-filter-tag"
                                        closable
                                        onClose={this.removeSelectedFilter.bind(this, currentFilter, filterGroup)}
                                        onKeyDown={this.removeSelectedFilter.bind(this, currentFilter, filterGroup)}
                                        tabIndex={0}
                                        closeIcon={<CloseCircleOutlined tabIndex={0}/>}
                                    >
                                        <Translate id={`filters.${currentFilter}.text`}/>
                                    </Tag>
                                ))}
                            </span>
                        )
                    ),
                )}
            </div>
        );
    }

    /**
     * Returns the header for the card list, the content changes depending on the active job type and device type
     * @param {boolean} filterVisible
     * @return {ReactElement}
     */
    getListHeader(filterVisible: boolean): ReactElement {
        const {
            translate, isTest, activeJobType, selectedFilters, sortDirection, isMobile,
        }: Props = this.props;
        const {translationPath}: typeof CardList = CardList;
        const {ASC, DESC}: typeof SortDirection = SortDirection;
        const {COMPLETED, IN_PROGRESS}: typeof ContentType = ContentType;
        const localePrefix: string = getLocalePrefix(isTest);

        return (
            <>
                <div className="df-item-list-header-container">
                    <div className="df-item-list-search">
                        <SearchField
                            onSearch={this.searchItems}
                            inputProps={{
                                placeholder: translate(`${translationPath}.${localePrefix}Search.text`) as string,
                                "aria-label": translate(`${translationPath}.${localePrefix}Search.text`) as string,
                            }}
                        />
                        {(isTest || activeJobType === COMPLETED) && (
                            <Button icon={<PlusOutlined/>} onClick={this.showFilterDrawer}>
                                <Translate id={`${translationPath}.addFilters.text`}/>
                            </Button>
                        )}
                    </div>
                    {(!isTest && activeJobType === IN_PROGRESS) && (
                        <div className="df-item-list-sort-container">
                            {isMobile
                                ? (
                                    <Button
                                        type="link"
                                        className="df-caret-toggle-btn"
                                        onClick={this.toggleSortDirection.bind(this)}
                                    >
                                        <CaretUpOutlined className={sortDirection === ASC ? "df-sort-active" : ""}/>
                                        <CaretDownOutlined className={sortDirection === DESC ? "df-sort-active" : ""}/>
                                    </Button>
                                )
                                : (
                                    <>
                                        <strong><Translate id={`${translationPath}.dateCreated.text`}/></strong>
                                        <Dropdown
                                            trigger={["click"]}
                                            overlayClassName="df-sort-direction-dropdown"
                                            overlay={(
                                                <Menu onClick={this.onSortDirectionChange.bind(this)} selectedKeys={[sortDirection]}>
                                                    {Object.keys(SortDirection).map((direction: string): ReactElement => (
                                                        <Menu.Item key={direction}>
                                                            <Translate id={`${translationPath}.${direction}.text`}/>
                                                        </Menu.Item>
                                                    ))}
                                                </Menu>
                                            )}
                                        >
                                            <Button type="link">
                                                <Translate id={`${translationPath}.${sortDirection}.text`}/>
                                                <DownOutlined/>
                                            </Button>
                                        </Dropdown>
                                    </>
                                )}
                        </div>
                    )}
                </div>
                {filterVisible && this.getSelectedFilters(selectedFilters)}
            </>
        );
    }

    /**
     * Toggles sort direction, and execute getJobList action with new sort direction
     */
    toggleSortDirection(): void {
        const {
            sortDirection: currentSortDirection, searchValue, filter, isTest, selectedFilters,
        }: Props = this.props;
        const {ASC, DESC}: typeof SortDirection = SortDirection;
        const sortDirection: string = currentSortDirection === DESC ? ASC : DESC;

        this.props.setSortDirection(sortDirection);
        this.props.getJobList({
            title: searchValue,
            sortDirection,
            filter,
            isTest,
            ...selectedFilters,
        });
    }

    /**
     * On remove tag, we updated selected filters list and refresh items list with new results
     * @param {string} value
     * @param {string} filterKey
     * @param {React.MouseEvent| React.KeyboardEvent} event
     */
    removeSelectedFilter(value: string, filterKey: string, event: React.MouseEvent | React.KeyboardEvent): void {
        const {key, type}: React.KeyboardEvent = event as React.KeyboardEvent;
        if (type === CLICK_EVENT || key === ENTER_KEY) {
            const {
                selectedFilters, filter, isTest, searchValue,
            }: Props = this.props;
            const updatedFilters: SelectedFiltersData = {
                ...selectedFilters,
                [filterKey]: selectedFilters[filterKey as keyof SelectedFiltersData].filter((item: string): boolean => item !== value),
            };
            this.props.getJobList({
                filter, isTest, title: searchValue, ...updatedFilters,
            });
            this.props.setSelectedFilters({...updatedFilters});
        }
    }

    /**
     * Sets search field value and triggers search for jobs or tests depending on current folder
     * @param {string} value
     */
    searchItems(value: string): void {
        const {
            getJobList: getJobListHandler, filter, isTest, selectedFilters,
        }: Props = this.props;
        getJobListHandler({
            title: value,
            filter,
            isTest,
            ...selectedFilters,
        });
        this.props.setJobSearch(value);
    }

    /**
     * Open drawer of current item on click or enter
     * @param {JobEntity} item
     * @param {React.MouseEvent | React.KeyboardEvent} event
     */
    openDrawer(item: JobEntity, event: React.MouseEvent | React.KeyboardEvent): void {
        const {key, type, currentTarget} = event as React.KeyboardEvent;
        const {activeElement}: Document = document;

        if (currentTarget === activeElement && (type === CLICK_EVENT || key === ENTER_KEY)) {
            this.props.setSelectedJob(item);
            this.props.showDetailsDrawer(DrawerTabs.STATS);
            activeElement.setAttribute("data-focus", "df-current");
        }
    }

    /**
     * Show placeholder icon with text when there is no data for the list
     *
     * @return {JSX.Element}
     */
    noContentRenderer(): JSX.Element {
        const {
            isLoading, activeJobType, activeTestType, searchValue, isTest, selectedFilters,
        }: Props = this.props;
        const isAnyFilterApplied: boolean = !!Object.values(selectedFilters).flat().length;
        let type: string = isTest ? activeTestType : activeJobType;
        const {SEARCH_NOT_FOUND, FILTER_NOT_FOUND}: typeof NoResultType = NoResultType;

        if (searchValue?.length) {
            type = SEARCH_NOT_FOUND;
        } else if (isAnyFilterApplied) {
            type = FILTER_NOT_FOUND;
        }

        return isLoading ? <></> : <ContentEmpty type={type}/>;
    }

    /**
     * On add filter button click we show filter drawer with appropriate available filter options
     */
    showFilterDrawer(): void {
        const {activeElement}: Document = document;
        if (activeElement) {
            activeElement.setAttribute("data-focus", "df-current");
        }
        this.props.setFiltersDrawerVisibility(true);
    }

    renderRow({
        key, style, parent, index,
    }: ListRowProps): React.ReactNode {
        const itemData: JobEntity = this.props.list[index];
        const isItemSelected: boolean = this.props.selectedJob?.id === itemData.id;
        const clsNames: string = classNames({
            "df-item-container": true,
            "df-item-selected": isItemSelected,
        });
        return (
            <CellMeasurer
                cache={CardList.cache}
                key={key}
                parent={parent}
            >
                <div style={{...style, display: "flex"}} key={key}>
                    <div
                        className={clsNames}
                        role="button"
                        tabIndex={0}
                        onClick={this.openDrawer.bind(this, itemData)}
                        onKeyDown={this.openDrawer.bind(this, itemData)}
                        onFocus={this.onFocusChange}
                    >
                        <CardItem data={itemData} hideBtnDropdown={false}/>
                    </div>
                </div>
            </CellMeasurer>
        );
    }

    render() {
        const {
            isLoading, list, isMobile, translate, isTest, selectedFilters, isExternalUrlLoading,
        }: Props = this.props;
        const localePrefix: string = getLocalePrefix(isTest);
        const filterVisibility: boolean = !!Object.values(selectedFilters).flat().length;
        // we keep track of filters and based on that adjust height of the virtual list (prevent cut off)
        const adjustedHeight: number = filterVisibility ? 70 : 30;
        return (
            <>
                {(isLoading || isExternalUrlLoading) && (<Spinner/>)}
                {this.getListHeader(filterVisibility)}
                <AutoSizer>
                    {({ width, height }: Size): React.ReactNode => (
                        <List
                            height={height - adjustedHeight}
                            width={width}
                            rowRenderer={this.renderRow.bind(this)}
                            deferredMeasurementCache={CardList.cache}
                            rowCount={list.length}
                            rowHeight={this.getAdjustedRowHeight(isMobile)}
                            overscanRowCount={10}
                            onScroll={this.onScrollHandler}
                            noRowsRenderer={this.noContentRenderer}
                            aria-label={translate(`${CardList.translationPath}.${localePrefix}List.text`) as string}
                        />
                    )}
                </AutoSizer>
            </>
        );
    }
}

function mapStateToProps(state: StoreState): StateProps {
    return {
        translate: getTranslate(state.localize),
        cursor: state.job.cursor,
        isLoading: state.mask.isLoading,
        isExternalUrlLoading: state.mask.isExternalUrlLoading,
        list: state.job.list,
        searchValue: state.job.searchValue,
        filter: state.job.filter,
        activeJobType: state.job.activeJobType,
        activeTestType: state.test.activeTestType,
        selectedJob: state.job.selectedJob,
        openEditor: state.job.openEditor,
        phaseConfigList: state.job.phaseConfigList,
        editors: state.serviceProperties.editors,
        selectedFilters: state.job.selectedFilters,
        externalJobUrl: state.job.externalJobUrl,
        sortDirection: state.job.sortDirection,
    };
}

const mapDispatchToProps: DispatchProps = {
    getJobList,
    setJobSearch,
    setSelectedJob,
    clearJobListData,
    showDetailsDrawer,
    openJobEditor,
    setJobTestType,
    setFiltersDrawerVisibility,
    setSelectedFilters,
    setExternalJobUrl,
    setSortDirection,
};

export default connect(mapStateToProps, mapDispatchToProps)(CardList);
